import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LogOut, Calendar, Users, IndianRupee, Star, MessageSquare, Mountain } from 'lucide-react';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TravelerDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [review, setReview] = useState({ rating: 5, comment: '' });

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const userResponse = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      setUser(userResponse.data);

      if (userResponse.data.role !== 'traveler') {
        await axios.post(`${BACKEND_URL}/api/users/switch-role?role=traveler`, {}, { withCredentials: true });
      }

      const bookingsResponse = await axios.get(`${BACKEND_URL}/api/bookings/traveler`, { withCredentials: true });
      setBookings(bookingsResponse.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Failed to fetch data');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleSubmitReview = async () => {
    if (!review.comment.trim()) {
      toast.error('Please write a review');
      return;
    }

    try {
      await axios.post(
        `${BACKEND_URL}/api/reviews`,
        {
          property_id: selectedBooking.property_id,
          booking_id: selectedBooking.booking_id,
          rating: review.rating,
          comment: review.comment
        },
        { withCredentials: true }
      );

      toast.success('Review submitted successfully!');
      setShowReviewDialog(false);
      setReview({ rating: 5, comment: '' });
      setSelectedBooking(null);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('You have already reviewed this property');
      } else {
        toast.error('Failed to submit review');
      }
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-spinner">
        <div className="w-16 h-16 border-4 border-[#8B2E2E] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div 
              className="flex items-center gap-2 cursor-pointer" 
              onClick={() => navigate('/')}
              data-testid="nav-logo"
            >
              <Mountain className="h-8 w-8 text-[#8B2E2E]" />
              <h1 className="text-2xl font-bold text-[#8B2E2E]" style={{fontFamily: 'Playfair Display, serif'}}>GoHimalayan</h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-[#4A4A4A]">Welcome, {user?.name}</span>
              <Button 
                variant="ghost"
                onClick={() => navigate('/host-dashboard')}
                data-testid="switch-to-host-btn"
              >
                Become a Host
              </Button>
              <Button 
                variant="ghost"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 
            className="text-3xl font-bold text-[#4A4A4A]"
            style={{fontFamily: 'Playfair Display, serif'}}
            data-testid="dashboard-title"
          >
            My Trips
          </h1>
          
          <Button 
            className="rounded-full bg-[#8B2E2E] hover:bg-[#6B1E1E] text-white px-6"
            onClick={() => navigate('/properties')}
            data-testid="explore-properties-btn"
          >
            Explore Properties
          </Button>
        </div>

        {/* Bookings Section */}
        <div data-testid="bookings-section">
          {bookings.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {bookings.map((booking, idx) => (
                <div 
                  key={booking.booking_id}
                  className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`booking-card-${idx}`}
                >
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 
                          className="text-xl font-bold text-[#4A4A4A] mb-1"
                          style={{fontFamily: 'Playfair Display, serif'}}
                        >
                          Booking #{booking.booking_id.slice(-6).toUpperCase()}
                        </h3>
                        <span className={`text-sm px-3 py-1 rounded-full inline-block ${
                          booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                          booking.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {booking.status}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-1 font-bold text-[#4A4A4A]">
                          <IndianRupee className="h-5 w-5" />
                          <span style={{fontFamily: 'JetBrains Mono, monospace'}}>{booking.total_amount.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3 mb-4">
                      <div className="flex items-center gap-2 text-[#4A4A4A]/70">
                        <Calendar className="h-5 w-5" />
                        <span>
                          {new Date(booking.check_in).toLocaleDateString()} - {new Date(booking.check_out).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-[#4A4A4A]/70">
                        <Users className="h-5 w-5" />
                        <span>{booking.guests} guests</span>
                      </div>
                    </div>

                    {booking.status === 'confirmed' && (
                      <Dialog open={showReviewDialog && selectedBooking?.booking_id === booking.booking_id} onOpenChange={setShowReviewDialog}>
                        <DialogTrigger asChild>
                          <Button 
                            className="w-full rounded-full border-2 border-[#8B2E2E] text-[#8B2E2E] hover:bg-[#8B2E2E] hover:text-white"
                            variant="outline"
                            onClick={() => setSelectedBooking(booking)}
                            data-testid={`write-review-btn-${idx}`}
                          >
                            <MessageSquare className="h-5 w-5 mr-2" />
                            Write a Review
                          </Button>
                        </DialogTrigger>
                        <DialogContent data-testid="review-dialog">
                          <DialogHeader>
                            <DialogTitle style={{fontFamily: 'Playfair Display, serif'}}>Write a Review</DialogTitle>
                          </DialogHeader>
                          
                          <div className="space-y-4 py-4">
                            <div>
                              <Label>Rating</Label>
                              <div className="flex gap-2 mt-2" data-testid="rating-stars">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Star 
                                    key={star}
                                    className={`h-8 w-8 cursor-pointer ${
                                      star <= review.rating 
                                        ? 'text-[#D4BF82] fill-current' 
                                        : 'text-gray-300'
                                    }`}
                                    onClick={() => setReview({...review, rating: star})}
                                    data-testid={`star-${star}`}
                                  />
                                ))}
                              </div>
                            </div>

                            <div>
                              <Label htmlFor="comment">Your Review</Label>
                              <Textarea 
                                id="comment"
                                value={review.comment}
                                onChange={(e) => setReview({...review, comment: e.target.value})}
                                placeholder="Share your experience..."
                                rows={4}
                                data-testid="review-comment-input"
                              />
                            </div>

                            <Button 
                              className="w-full rounded-full bg-[#8B2E2E] hover:bg-[#6B1E1E] text-white"
                              onClick={handleSubmitReview}
                              data-testid="submit-review-btn"
                            >
                              Submit Review
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl p-12 text-center" data-testid="no-bookings">
              <Calendar className="h-16 w-16 text-[#4A4A4A]/30 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-[#4A4A4A] mb-2">No trips yet</h3>
              <p className="text-[#4A4A4A]/70 mb-6">Start exploring amazing properties in the Himalayas</p>
              <Button 
                className="rounded-full bg-[#8B2E2E] hover:bg-[#6B1E1E] text-white px-8"
                onClick={() => navigate('/properties')}
                data-testid="start-exploring-btn"
              >
                Start Exploring
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TravelerDashboard;
